import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ScrollToTop } from './components/shared/ScrollToTop'
import { ErrorBoundary } from './components/shared/ErrorBoundary'
import { createErrorHandler } from './lib/error-reporting'
import { ProtectedRoute } from './components/shared/ProtectedRoute'
import { PublicOnlyRoute } from './components/shared/PublicOnlyRoute'
import { ThemeURLHandler } from './components/ThemeURLHandler'
import { Loader2 } from 'lucide-react'

// Critical-path imports (always in the main bundle)
import { DashboardLayout } from './layouts/DashboardLayout'
import { DashboardPage } from './pages/DashboardPage'
import { NotFoundPage } from './pages/NotFoundPage'

// Auth pages — small, fast-loading, keep in main bundle
import { SignInPage } from './modules/auth/SignInPage'
import { SignUpPage } from './modules/auth/SignUpPage'

// Lazy-loaded pages — each gets its own chunk, loaded on first visit
const ForgotPasswordPage = lazy(() => import('./modules/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('./modules/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })))
const VerifyEmailPage = lazy(() => import('./modules/auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })))
const SettingsPage = lazy(() => import('./modules/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const AdminPage = lazy(() => import('./modules/admin/pages/AdminPage').then(m => ({ default: m.AdminPage })))
const ChatPage = lazy(() => import('./modules/chat/pages/ChatPage').then(m => ({ default: m.ChatPage })))
const ExtractPage = lazy(() => import('./modules/chat/pages/ExtractPage').then(m => ({ default: m.ExtractPage })))
const ProjectPage = lazy(() => import('./modules/projects/pages/ProjectPage').then(m => ({ default: m.ProjectPage })))
const ActivityPage = lazy(() => import('./modules/activity/pages/ActivityPage').then(m => ({ default: m.ActivityPage })))
const FilesPage = lazy(() => import('./modules/files/pages/FilesPage').then(m => ({ default: m.FilesPage })))
const SkillsPage = lazy(() => import('./modules/skills/pages/SkillsPage').then(m => ({ default: m.SkillsPage })))
const NotificationsPage = lazy(() => import('./modules/notifications/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const ConnectorsPage = lazy(() => import('./modules/connectors/pages/ConnectorsPage').then(m => ({ default: m.ConnectorsPage })))
// Ambient Notes domain pages
const RecordPage = lazy(() => import('./modules/record/pages/RecordPage').then(m => ({ default: m.RecordPage })))
const AmbientProfilePage = lazy(() => import('./modules/record/pages/ProfilePage').then(m => ({ default: m.ProfilePage })))
const SessionsListPage = lazy(() => import('./modules/sessions/pages/SessionsListPage').then(m => ({ default: m.SessionsListPage })))
const SessionReviewPage = lazy(() => import('./modules/sessions/pages/SessionReviewPage').then(m => ({ default: m.SessionReviewPage })))
const DemoPage = lazy(() => import('./pages/DemoPage').then(m => ({ default: m.DemoPage })))
const SamplesPage = lazy(() => import('./pages/SamplesPage').then(m => ({ default: m.SamplesPage })))
const ClaimPage = lazy(() => import('./pages/ClaimPage').then(m => ({ default: m.ClaimPage })))
const ComponentsPage = lazy(() => import('./pages/ComponentsPage').then(m => ({ default: m.ComponentsPage })))
const StyleGuidePage = lazy(() => import('./pages/StyleGuidePage').then(m => ({ default: m.StyleGuidePage })))

function PageSpinner() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Loader2 className="size-6 animate-spin text-muted-foreground" />
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary onError={createErrorHandler()}>
      <TooltipProvider delayDuration={200}>
      <BrowserRouter>
        <ScrollToTop />
        <ThemeURLHandler />
        <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* Landing page is the demo page — zero friction, recording is
              front and centre. Marketing content renders below when idle.
              /demo still exists for bookmarks and a leaner view. */}
          <Route path="/" element={<DemoPage />} />

          {/* Full sample library — browse all 14 canned scenarios.
              Each card links back to `/?sample=<slug>` to auto-play. */}
          <Route path="/samples" element={<SamplesPage />} />

          {/* Auth routes (standalone, no layout) — bounce already-signed-in
              users back to the dashboard so /sign-in and /sign-up never show
              a confusing form to a returning user. */}
          <Route
            path="/sign-in"
            element={
              <PublicOnlyRoute>
                <SignInPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/sign-up"
            element={
              <PublicOnlyRoute>
                <SignUpPage />
              </PublicOnlyRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Public no-auth demo — anyone can try, sessions saved under a
              shared demo user. Intentionally outside PublicLayout/auth. */}
          <Route path="/demo" element={<DemoPage />} />

          {/* Post-signin claim hand-off — /claim?session=<id> moves the
              demo session to the currently signed-in user. Runs once then
              redirects to /dashboard/sessions/<id>. */}
          <Route path="/claim" element={<ClaimPage />} />

          {/* Protected dashboard routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard index page */}
            <Route index element={<DashboardPage />} />

            {/* Settings module - profile, password, theme, etc. */}
            <Route path="settings" element={<SettingsPage />} />

            {/* Admin panel - users, features, tokens */}
            <Route path="admin" element={<AdminPage />} />

            {/* Ambient Notes — record, sessions list, session review */}
            <Route path="record" element={<RecordPage />} />
            <Route path="sessions" element={<SessionsListPage />} />
            <Route path="sessions/:id" element={<SessionReviewPage />} />

            {/* AI Chat — single route with optional conversationId so
                transitioning from /chat to /chat/:id (post-first-send)
                doesn't remount ChatPage and wipe in-flight streaming
                state. Two separate routes was the C1 regression cause:
                navigate(replace:true) between Route entries unmounts. */}
            <Route path="chat/:conversationId?" element={<ChatPage />} />
            <Route path="projects/:id" element={<ProjectPage />} />
            <Route path="extract" element={<ExtractPage />} />

            {/* Activity log */}
            <Route path="activity" element={<ActivityPage />} />

            {/* Files */}
            <Route path="files" element={<FilesPage />} />

            {/* Skills — agentskills.io registry UI */}
            <Route path="skills" element={<SkillsPage />} />

            {/* Notifications full history (bell dropdown shows top 10) */}
            <Route path="notifications" element={<NotificationsPage />} />

            {/* MCP Connectors — per-user OAuth + bearer connections */}
            <Route path="connectors" element={<ConnectorsPage />} />

            {/* Ambient Notes domain profile — trade / role / company / region.
                Distinct from /dashboard/settings (starter's name/avatar/password). */}
            <Route path="profile" element={<AmbientProfilePage />} />

            {/* Component showcase for development reference */}
            <Route path="components" element={<ComponentsPage />} />

            {/* Style guide for development */}
            <Route path="style-guide" element={<StyleGuidePage />} />

            {/* Dashboard catch-all — keeps authed users inside the shell.
                Silently redirecting to "/" looked like a crash to users who
                followed a stale bookmark. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Public catch-all — same page, unauthed shell. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </TooltipProvider>
    </ErrorBoundary>
  )
}

export default App
